/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { useState, useEffect } from "react";
import { breakpoints } from "style";
import MemberTestimonialCard from "./MemberTestimonialCard";
import CarouselNavigationButton from "./CarouselNavigationButton";

/**
 * MemberTestimonialsCarousel
 *
 * @param   {Array}   testimonials
 */
const MemberTestimonialsCarousel = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3);

  // Detect screen size and set cards per view
  useEffect(() => {
    const updateCardsPerView = () => {
      const width = window.innerWidth;
      const mobileBreakpoint = parseInt(breakpoints.mobile);
      const tabletBreakpoint = parseInt(breakpoints.tablet);

      // Mobile: 1 card - Tablet: 2 cards - Desktop: 3 cards
      if (width <= mobileBreakpoint) {
        setCardsPerView(1);
      } else if (width <= tabletBreakpoint) {
        setCardsPerView(2);
      } else {
        setCardsPerView(3);
      }
    };

    updateCardsPerView();
    window.addEventListener("resize", updateCardsPerView);
    return () => window.removeEventListener("resize", updateCardsPerView);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - cardsPerView);

  const handlePrevious = () => setCurrentIndex((prev) => Math.max(0, prev - 1));
  const handleNext = () => setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));

  // Calculate transform based on cards per view
  const translateX = -(currentIndex * (100 / cardsPerView));

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  // Only show navigation if there are more cards than can be displayed
  const showNavigation = testimonials.length > cardsPerView;

  return (
    <div css={styles.container}>
      <div css={styles.carouselWrapper}>
        {showNavigation && (
          <CarouselNavigationButton
            direction="left"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          />
        )}

        <div css={styles.viewport}>
          <div
            css={styles.track}
            style={{ transform: `translateX(${translateX}%)` }}
          >
            {testimonials.map((testimonial) => (
              <MemberTestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>

        {showNavigation && (
          <CarouselNavigationButton
            direction="right"
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  container: css`
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 40px 0;

    @media (max-width: ${breakpoints.tablet}) {
      padding: 30px 0;
    }

    @media (max-width: ${breakpoints.mobile}) {
      padding: 20px 0;
    }
  `,
  carouselWrapper: css`
    position: relative;
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 0 60px;

    @media (max-width: ${breakpoints.tablet}) {
      padding: 0 40px;
      gap: 16px;
    }

    @media (max-width: ${breakpoints.mobile}) {
      padding: 0;
      gap: 4px;
    }
  `,
  viewport: css`
    flex: 1;
    overflow: hidden;
    position: relative;
  `,
  track: css`
    display: flex;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
  `,
};

export default MemberTestimonialsCarousel;
